// Single source of truth for turning a glossary term's display name into its
// URL slug.
//
// WHY THIS FILE EXISTS (SEO Roadmap P7a)
// -------------------------------------
// The slug transform used to be copy-pasted, byte-for-byte, across 9 call
// sites: sitemap.ts, the glossary index, the glossary term page (params +
// lookup + related terms), the search registry, related-terms.ts, the glossary
// search client, and the calculator template's glossary linker. Any drift
// between two copies silently breaks internal links and 404s indexed pages
// (a slug produced by the sitemap that a page component cannot resolve).
// Centralizing here guarantees every producer and consumer of a glossary slug
// agrees exactly.
//
// SLUG RULES (SEO Roadmap P7b)
// ----------------------------
//   1. Transliterate German umlauts / eszett to ASCII digraphs
//      (ae/oe/ue/ss) — done first so ü becomes "ue", not "u".
//   2. NFKD-normalize and strip the remaining combining marks, so other
//      diacritics degrade to their base letter (é→e, ç→c, ñ→n, å→a …).
//   3. Delete intra-word punctuation — parentheses and apostrophes — so it
//      fuses instead of leaving a gap: "401(k)"→"401k",
//      "Home Buyers Plan (HBP)"→"home-buyers-plan-hbp".
//   4. Lowercase.
//   5. Replace every remaining run of non-[a-z0-9] characters with a single "-".
//   6. Trim leading/trailing "-".
//
// The old function skipped steps 1–3 and step 6, which produced two defects on
// ~80 indexed URLs: eaten diacritics (ü→"-", e.g. "s-ule-3a-investment") and
// trailing dashes (parentheses at the end, e.g. "capital-gains-tax-cgt-").
//
// Verified outputs (the canonical slug behavior for glossary URLs):
//   "Growth Period (Trump Account)"          -> "growth-period-trump-account"
//   "401(k)"                                  -> "401k"
//   "Frühstart-Rente (Early Start Pension)"  -> "fruehstart-rente-early-start-pension"
//   "Säule 3a Investment"                     -> "saeule-3a-investment"

// Applied before Unicode normalization so umlauts romanize the German way
// (ü→ue) rather than losing the mark (ü→u). Capital forms included for terms
// that begin with an umlaut.
const GERMAN_TRANSLITERATION: ReadonlyArray<readonly [RegExp, string]> = [
  [/ä/g, "ae"],
  [/ö/g, "oe"],
  [/ü/g, "ue"],
  [/ß/g, "ss"],
  [/Ä/g, "Ae"],
  [/Ö/g, "Oe"],
  [/Ü/g, "Ue"],
  [/ẞ/g, "Ss"],
];

// Punctuation that sits *inside* a token and should fuse (be deleted) rather
// than become a separator: parentheses and straight/typographic apostrophes.
const INTRA_WORD_PUNCTUATION = /['\u2019()]/g;

// Combining diacritical marks left behind by NFKD decomposition.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Convert a glossary term display name into its canonical URL slug.
 * Deterministic and idempotent: slugify(slugify(x)) === slugify(x).
 */
export function slugify(input: string): string {
  if (!input) return "";

  let s = input;
  for (const [pattern, replacement] of GERMAN_TRANSLITERATION) {
    s = s.replace(pattern, replacement);
  }

  return s
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .replace(INTRA_WORD_PUNCTUATION, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
